import React from "react";
import { Lock, DoorOpen, MapPin, Plus, X, ChevronDown } from "lucide-react";
import { Card, Input, Toggle, Textarea, RepeatableGroup, PillGroup, Select } from "./FormComponents";

export const Step2 = ({ data, updateData, isLight, showValidation }) => {
  const handleChange = (field, value) => {
    updateData("buildingAccess", { ...data, [field]: value });
  };

  const handleNestedChange = (parent, field, value) => {
    updateData("buildingAccess", {
      ...data,
      [parent]: {
        ...data[parent],
        [field]: value
      }
    });
  };

  const handleRouteUpdate = (index, field, value) => {
    const newRoutes = [...data.arrivalRoutes];
    newRoutes[index] = { ...newRoutes[index], [field]: value };
    handleChange("arrivalRoutes", newRoutes);
  };

  return (
    <div className="flex flex-col gap-8">
      <Card title="Building Entry" icon={Lock} subtitle="Help guests find and enter the building." isLight={isLight}>
        <div className="flex flex-col gap-8">
          <Input
            label="What does the building look like from outside?"
            placeholder="Green awning, blue entrance door next to a bakery"
            value={data.buildingRecognition}
            onChange={(e) => handleChange("buildingRecognition", e.target.value)}
            isLight={isLight}
            showValidation={showValidation}
          />
          <PillGroup
            label="How can guests reach their floor?"
            required
            options={["Elevator Only", "Stairs Only", "Both (Stairs & Elevator)", "None (Ground Floor)"]}
            value={data.buildingAccessType}
            onChange={(val) => handleChange("buildingAccessType", val)}
            isLight={isLight}
            showValidation={showValidation}
          />

          <Toggle
            label="Is there a main building entrance door before guests reach the accommodation door?"
            value={data.hasMainEntrance}
            onChange={(val) => {
              handleChange("hasMainEntrance", val);
              if (!val) {
                // Clear building entrance data in Check-in (Step 4)
                updateData("checkIn", (prevCheckIn) => ({
                  ...prevCheckIn,
                  selfCheckIn: {
                    ...prevCheckIn.selfCheckIn,
                    mainEntranceAccessType: "No, guests can enter the building freely",
                    mainEntranceAccessFrom: "",
                    mainEntranceAccessUntil: "",
                    // Also clear building entrance device if multi-device is active
                    buildingEntrance: null
                  }
                }));
                // Clear building exit data in Check-out (Step 5)
                updateData("checkOut", (prevCheckOut) => ({
                  ...prevCheckOut,
                  buildingExit: {
                    accessType: "No, the building exit opens freely from inside",
                    accessFrom: "",
                    accessUntil: "",
                    exitInstructions: ""
                  }
                }));
              }
            }}
            isLight={isLight}
          />

          <Toggle
            label="Is there an intercom or buzzer at the main building entrance?"
            value={data.intercom.available}
            onChange={(val) => {
              const updates = { available: val };
              if (!val) updates.instructions = "";
              handleChange("intercom", { ...data.intercom, ...updates });
            }}
            isLight={isLight}
          >
            <Input
              label="Intercom label"
              required={data.intercom.available}
              placeholder="Room 101"
              value={data.intercom.instructions}
              onChange={(e) => handleNestedChange("intercom", "instructions", e.target.value)}
              isLight={isLight}
              showValidation={showValidation}
            />
          </Toggle>

          <Toggle
            label="Wheelchair accessible?"
            value={data.wheelchairAccessible}
            onChange={(val) => handleChange("wheelchairAccessible", val)}
            isLight={isLight}
          />
        </div>
      </Card>

      <Card title="Room Access" icon={DoorOpen} subtitle="Getting from the entrance to their room." isLight={isLight}>
        <div className="flex flex-col gap-6">
          <Textarea
            label="Instructions to find the room"
            required
            placeholder="Enter main entrance. Take elevator to 1st floor. Turn right. Apartment 101 is the first door on the left."
            value={data.roomFindingInstructions}
            onChange={(e) => handleChange("roomFindingInstructions", e.target.value)}
            isLight={isLight}
            showValidation={showValidation}
          />
        </div>
      </Card>

      <Card title="Arrival Routes" icon={MapPin} subtitle="How guests reach the property from major arrival points." isLight={isLight}>
        <RepeatableGroup
          label="Routes"
          items={data.arrivalRoutes}
          isLight={isLight}
          onAdd={() => handleChange("arrivalRoutes", [...data.arrivalRoutes, { id: Date.now().toString(), from: "", method: "", instructions: "", time: "", costAmount: "", costCurrency: "€" }])}
          onRemove={(index) => handleChange("arrivalRoutes", data.arrivalRoutes.filter((_, i) => i !== index))}
          renderItem={(route, index) => (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="From"
                required
                placeholder="Athens International Airport"
                value={route.from}
                onChange={(e) => handleRouteUpdate(index, "from", e.target.value)}
                isLight={isLight}
                showValidation={showValidation}
              />
              <Input
                label="Method"
                required
                placeholder="Metro Line 3"
                value={route.method}
                onChange={(e) => handleRouteUpdate(index, "method", e.target.value)}
                isLight={isLight}
                showValidation={showValidation}
              />
              <div className="md:col-span-2">
                <Textarea
                  label="Step-by-step instructions"
                  value={route.instructions}
                  onChange={(e) => handleRouteUpdate(index, "instructions", e.target.value)}
                  isLight={isLight}
                  showValidation={showValidation}
                />
              </div>
              <div className="flex flex-col gap-4">
                <label className={`text-[10px] uppercase tracking-[0.18em] font-bold ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>
                  Travel time
                </label>
                <div className="flex gap-4 items-center">
                  <div className="w-32">
                    <Input
                      placeholder="45"
                      value={route.time}
                      isLight={isLight}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, "");
                        handleRouteUpdate(index, "time", val);
                      }}
                    />
                  </div>
                  <span className={`text-xs font-bold uppercase tracking-widest ${isLight ? "text-neutral-400" : "text-neutral-500"}`}>minutes</span>
                </div>
              </div>
              <div className="flex gap-4 items-end">
                <Input
                  label="Approximate cost"
                  placeholder="9"
                  value={route.costAmount}
                  onChange={(e) => handleRouteUpdate(index, "costAmount", e.target.value)}
                  isLight={isLight}
                />
                <Select
                  label="Currency"
                  options={["€", "$", "£"]}
                  className="w-32"
                  value={route.costCurrency}
                  hidePlaceholder
                  onChange={(e) => handleRouteUpdate(index, "costCurrency", e.target.value)}
                  isLight={isLight}
                />
              </div>
            </div>
          )}
        />
      </Card>
    </div>
  );
};
